/**
 * WebAuthn helper functions for biometric authentication
 */
import { getDeviceToken } from "./deviceToken";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert ArrayBuffer to base64url string
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

// Convert base64url string to ArrayBuffer
function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base64.length % 4 === 0 ? "" : "=".repeat(4 - (base64.length % 4));
  const raw = atob(base64 + pad);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr.buffer;
}

async function safeFetch(url, options = {}) {
  const res = await fetch(url, options);
  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    data = { detail: text };
  }
  if (!res.ok) throw new Error(data.detail || `Errore ${res.status}`);
  return data;
}

/**
 * Check if WebAuthn is supported and if biometric (platform) authenticator is available
 */
export async function isBiometricAvailable() {
  if (!window.PublicKeyCredential) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Check if a user has biometric credentials registered
 */
export async function checkUserBiometric(username) {
  try {
    const data = await safeFetch(`${API}/auth/webauthn/check/${username}`);
    return data.has_biometric;
  } catch {
    return false;
  }
}

/**
 * Register a new biometric credential (requires authenticated user)
 */
export async function registerBiometric(token) {
  // 1. Get registration options from server
  const options = await safeFetch(`${API}/auth/webauthn/register-options`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });

  // 2. Convert server options for browser API
  const publicKey = {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64urlToBuffer(options.user.id),
    },
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  };

  // 3. Create credential via browser (triggers biometric prompt)
  const credential = await navigator.credentials.create({ publicKey });

  // 4. Serialize credential for server
  const credentialJSON = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64url(credential.response.attestationObject),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
    },
  };

  if (credential.response.getTransports) {
    credentialJSON.response.transports = credential.response.getTransports();
  }

  // 5. Send to server for verification
  return await safeFetch(`${API}/auth/webauthn/register-complete`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ credential: credentialJSON }),
  });
}

/**
 * Login with biometric credential
 */
export async function loginWithBiometric(username) {
  // 1. Get authentication options
  const options = await safeFetch(`${API}/auth/webauthn/login-options`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username }),
  });

  // 2. Convert for browser API
  const publicKey = {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  };

  // 3. Get credential via browser (triggers biometric prompt)
  const assertion = await navigator.credentials.get({ publicKey });

  // 4. Serialize for server
  const assertionJSON = {
    id: assertion.id,
    rawId: bufferToBase64url(assertion.rawId),
    type: assertion.type,
    response: {
      authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
      clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
      signature: bufferToBase64url(assertion.response.signature),
    },
  };
  if (assertion.response.userHandle) {
    assertionJSON.response.userHandle = bufferToBase64url(assertion.response.userHandle);
  }

  // 5. Verify on server (include device_token for device binding)
  const device_token = getDeviceToken();
  return await safeFetch(`${API}/auth/webauthn/login-complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, credential: assertionJSON, device_token }),
  });
}

/**
 * Get registered credentials
 */
export async function getCredentials(token) {
  try {
    return await safeFetch(`${API}/auth/webauthn/credentials`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    return [];
  }
}

/**
 * Delete a credential
 */
export async function deleteCredential(token, credId) {
  return await safeFetch(`${API}/auth/webauthn/credentials/${credId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
}
